import { createAssetPackageReader } from './asset_package_reader.mjs';

const dot = (a, b) => a.reduce((sum, n, i) => sum + n * b[i], 0);
const cross = (a, b) => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const identity = [[1, 0, 0], [0, 1, 0], [0, 0, 1]];
// Actual legacy SVG/DXF axes (including the converter's front reflection).
const legacy = {
  top: [[0, 1, 0], [1, 0, 0], [0, 0, 1]], bottom: [[0, -1, 0], [-1, 0, 0], [0, 0, 1]],
  front: [[0, 0, 1], [1, 0, 0], [0, 1, 0]], back: [[0, 0, -1], [1, 0, 0], [0, 1, 0]],
  left: [[-1, 0, 0], [0, 0, -1], [0, 1, 0]], right: [[1, 0, 0], [0, 0, 1], [0, 1, 0]]
};
const toSource = (rows, vector) => identity.map((_, i) => rows.reduce((sum, row, j) => sum + row[i] * vector[j], 0));
const clean = n => Math.abs(n) < 1e-10 ? 0 : n;

/** Match visible faces before rotating their screen coordinates; never turn an underside into a top view. */
export function assetPreviewFrames(recipe, compatibility) {
  const m = recipe?.transfer?.transferredMmToAsset;
  if (!Array.isArray(m) || m.length !== 16) throw Error('Importausrichtung fehlt.');
  const canonical = [0, 1, 2].map(i => [m[i], m[i + 4], m[i + 8]]);
  const correction = compatibility?.request?.orientationCorrection;
  const old = correction ? [cross(correction.topAxis, correction.frontAxis), correction.topAxis, correction.frontAxis] : identity;
  const frames = {};
  for (const view of recipe.projection.views) {
    const normal = toSource(canonical, view.towardViewer);
    const match = Object.entries(legacy).find(([, [n]]) => dot(normal, toSource(old, n)) > 1 - 1e-8);
    if (!match) throw Error('Keine passende Projektionsansicht für diese Importausrichtung.');
    const [artifactView, [, x, y]] = match;
    const right = toSource(canonical, view.right), up = toSource(canonical, view.up);
    const sx = toSource(old, x), sy = toSource(old, y);
    // SVG and the GPU viewport both have downward-positive screen Y.
    frames[view.viewId] = { artifactView, transform: [dot(right, sx), -dot(up, sx), dot(right, sy), -dot(up, sy), 0, 0].map(clean) };
  }
  return frames;
}

export async function loadAssetPreviewFrames(item, providedReader = null, providedPackage = null) {
  if (!item?.currentRevisionId) return null; // Pre-revision assets retain their documented legacy views.
  const ownReader = !providedReader;
  const reader = providedReader || createAssetPackageReader();
  try {
    const resolved = providedPackage || await reader.resolve({ assetObjectId: Number(item.id), revisionId: item.currentRevisionId });
    if (resolved.status !== 'ready') throw Error('Importausrichtung konnte nicht geladen werden.');
    const results = await Promise.all(['recipe', 'compatibilityRecipe'].map(kind => reader.readArtifact(resolved.reference, kind)));
    if (results.some(result => result.status !== 'ready')) throw Error('Importausrichtung konnte nicht geprüft werden.');
    return assetPreviewFrames(...results.map(result => JSON.parse(new TextDecoder().decode(result.bytes))));
  } finally { if (ownReader) reader.dispose(); }
}

export function previewFrame(frames, view) {
  return frames?.[view] || { artifactView: view, transform: [1, 0, 0, 1, 0, 0] };
}

/** Rotate a presentation copy, including its fit bounds. Region IDs and native arcs stay intact. */
export function orientPreviewSvg(text, transform) {
  const document = new DOMParser().parseFromString(text, 'image/svg+xml');
  const svg = document.documentElement;
  const box = svg.getAttribute('viewBox')?.trim().split(/[\s,]+/).map(Number);
  if (svg.localName !== 'svg' || box?.length !== 4 || !box.every(Number.isFinite)) return text;
  const [a, b, c, d, e, f] = transform, [x, y, w, h] = box;
  const points = [[x, y], [x + w, y], [x, y + h], [x + w, y + h]].map(([px, py]) => [a * px + c * py + e, b * px + d * py + f]);
  const xs = points.map(p => p[0]), ys = points.map(p => p[1]);
  svg.setAttribute('viewBox', [Math.min(...xs), Math.min(...ys), Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys)].join(' '));
  const group = document.createElementNS('http://www.w3.org/2000/svg', 'g');
  group.setAttribute('transform', `matrix(${transform.join(' ')})`);
  group.setAttribute('data-preview-orientation', '');
  while (svg.firstChild) group.append(svg.firstChild);
  svg.append(group);
  return new XMLSerializer().serializeToString(svg);
}
